import { useMemo } from "react";
import { TAILWIND_PROPERTIES, TAILWIND_LOOKUP } from "@/lib/tailwind";
import { ClassType, ClassTypeWithPropertyType } from "@/tailwind/tailwindUtils";
import { ALL_MODIFIERS_SET } from "./useModifiers";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import {
  PropertyType,
  tailwindFilterFunctions,
} from "@/tailwind/tailwindUtils";

const filterFunctionList = Object.values(tailwindFilterFunctions);

export const useTailwindProperties = (
  type: PropertyType
): ClassTypeWithPropertyType[] => {
  const tailwindClasses = useCodebaseStore((state) => state.tailwindClasses);

  return useMemo(() => {
    let allProperties = TAILWIND_PROPERTIES;
    if (tailwindClasses && tailwindClasses.length > 0) {
      allProperties = tailwindClasses;
    }

    const filteredProperties = allProperties.filter(
      tailwindFilterFunctions[type]
    );
    return filteredProperties;
  }, [type, tailwindClasses]);
};

export const useUnknownTailwindClasses = (classList: string[]): string[] => {
  const tailwindClasses = useCodebaseStore((state) => state.tailwindClasses);

  return useMemo(() => {
    if (!classList) {
      return [];
    }

    return classList.filter((className: string) => {
      if (
        className.startsWith("tempo-") ||
        className.startsWith("arb89-") ||
        className.startsWith("temp-")
      ) {
        return false;
      }

      // Note, all text properties are supported in the styling panel,
      // but it's difficult to filter for which property exactly is being used.
      // So filter here instead of adding to the filter functions
      if (className.startsWith("text-")) {
        return false;
      }

      // Similarly, all border properties are supported
      if (className.startsWith("border-")) {
        return false;
      }

      // All flex properties are supported
      if (className.startsWith("flex")) {
        return false;
      }

      // Strip modifier name from class
      ALL_MODIFIERS_SET.forEach((modifier) => {
        if (className.startsWith(`${modifier}:`)) {
          // Replace from start of string
          className = className.replace(`${modifier}:`, "");
        }
      });

      // If there are custom properties, directly check if the class is in the list
      if (tailwindClasses) {
        // TODO(nhanthai-tailwind): Use set maybe
        if (
          tailwindClasses.find((prop: ClassType) => prop.tailwind === className)
        ) {
          return false;
        }
      }

      return !filterFunctionList.find((filterFn: any) => {
        return filterFn({ tailwind: className, css: "" });
      });
    });
  }, [classList, tailwindClasses]);
};
